import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { focusNext } from "@/lib/formKeys"

const SEARCH_PH = { id: "Ketik untuk mencari…", en: "Type to search…", zh: "输入以搜索…" }

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

// Search box in every dropdown (same feel as the company picker): type to filter, ↑/↓ PageUp/PageDown move the highlight
// while the cursor stays in the box, Enter picks, Esc closes. Rendered inside Content, so it starts fresh on every open.
function SearchBody({ position, children }) {
  const vp = React.useRef(null);
  const input = React.useRef(null);
  const grab = React.useRef(true);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(-1);
  const visible = () => [...(vp.current?.querySelectorAll('[role="option"]') || [])]
    .filter((o) => o.style.display !== "none" && !o.hasAttribute("data-disabled"));
  React.useEffect(() => {
    const el = vp.current;
    if (!el) return;
    const ql = q.trim().toLowerCase();
    el.querySelectorAll('[role="option"]').forEach((o) => { o.style.display = !ql || o.textContent.toLowerCase().includes(ql) ? "" : "none"; });
    const v = visible();
    setActive(!v.length ? -1 : ql ? 0 : Math.max(0, v.findIndex((o) => o.getAttribute("data-state") === "checked")));
  }, [q, children]);
  React.useEffect(() => {
    vp.current?.querySelectorAll("[data-kbd]").forEach((o) => o.removeAttribute("data-kbd"));
    const o = visible()[active];
    if (o) { o.setAttribute("data-kbd", ""); o.scrollIntoView({ block: "nearest" }); }
  }, [active, q]);
  React.useEffect(() => {
    // Radix focuses the selected option right after opening; keep the cursor in the search box instead.
    const root = input.current?.closest('[role="listbox"]');
    const focus = () => input.current?.focus({ preventScroll: true });
    const onFocus = (e) => { if (grab.current && e.target !== input.current) focus(); };
    const stop = () => { grab.current = false; };
    root?.addEventListener("focusin", onFocus);
    root?.addEventListener("pointermove", stop);
    const raf = requestAnimationFrame(focus);
    const t = setTimeout(stop, 600);
    return () => { root?.removeEventListener("focusin", onFocus); root?.removeEventListener("pointermove", stop); cancelAnimationFrame(raf); clearTimeout(t); };
  }, []);
  const onInputKey = (e) => {
    if (e.key === "Escape" || e.key === "Tab") return;
    grab.current = false;
    const step = { ArrowDown: 1, ArrowUp: -1, PageDown: 8, PageUp: -8 }[e.key];
    const v = visible();
    if (step) {
      e.preventDefault();
      if (v.length) setActive((a) => Math.max(0, Math.min(v.length - 1, (a < 0 ? (step > 0 ? -1 : v.length) : a) + step)));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = v[active] || v[0];
      if (o) {
        o.focus();
        o.click();
        // Tally-style: after picking with Enter, continue to the next field of the form.
        setTimeout(() => { const a = document.activeElement; if (a?.getAttribute("role") === "combobox" && a.closest("[role=dialog]")) focusNext(a); }, 50);
      }
    }
    e.stopPropagation();
  };
  const typeToSearch = (e) => {
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    setQ((x) => x + e.key);
    input.current?.focus({ preventScroll: true });
  };
  return (
    <>
      <div className="sticky top-0 z-10 border-b border-border bg-popover p-1">
        <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey} placeholder={SEARCH_PH[localStorage.getItem("fiskal_lang")] || SEARCH_PH.id}
          className="h-8 w-full rounded-sm border border-input bg-transparent px-2 text-sm outline-none focus:ring-1 focus:ring-ring" data-testid="select-search-input" />
      </div>
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport ref={vp} onKeyDown={typeToSearch}
        className={cn("p-1", position === "popper" &&
          "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")}>
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </>
  );
}
const SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}>
      <SearchBody position={position}>{children}</SearchBody>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[kbd]:bg-accent data-[kbd]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
